import React, { useEffect, useState } from "react";
import {
  Package, Layers, Globe2, MapPin, Users, UserCircle2, Receipt, UserX, Building2,
} from "lucide-react";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { KpiCard } from "../components/KpiCard";
import { InfoTooltip } from "../components/InfoTooltip";
import { toast } from "sonner";

const SUPPLIER_CATEGORY_LABELS = {
  business: "Business",
  registered_individual: "Registered Individual",
  unregistered_individual: "Unregistered Individual",
  student_club: "Student Club",
  educational_institute: "Educational Institute",
  government_entity: "Government Entity",
  non_profit: "Non Profit Organization",
  other: "Other",
};

export default function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let active = true;
    api.get("/overview/stats")
      .then(({ data }) => { if (active) setStats(data); })
      .catch(() => { if (active) toast.error("Unable to load overview statistics."); });
    return () => { active = false; };
  }, []);

  return (
    <div data-testid="overview-page">
      <PageHeader
        title="Overview"
        description="A high-level pulse of the marketplace — counts across products, options, geographies, suppliers, customers, bookings and cancellations."
      />

      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-6">
        <KpiCard
          label="Products"
          value={stats?.products ?? "—"}
          icon={Package}
          info="Total number of experiences (products) listed on the marketplace, across all statuses."
          testId="overview-products"
        />
        <KpiCard
          label="Options"
          value={stats?.options ?? "—"}
          icon={Layers}
          info="Total number of product option variants, excluding deleted products."
          testId="overview-options"
        />
        <KpiCard
          label="Countries"
          value={stats?.countries ?? "—"}
          icon={Globe2}
          info={stats?.countries_list?.join(", ") || "Unique countries in supplier profiles."}
          testId="overview-countries"
        />
        <KpiCard
          label="Cities"
          value={stats?.cities ?? "—"}
          icon={MapPin}
          info={stats?.cities_list?.join(", ") || "Unique primary cities across non-deleted products."}
          testId="overview-cities"
        />
        <KpiCard
          label="Suppliers"
          value={stats?.suppliers ?? "—"}
          icon={Users}
          info="Total number of suppliers across all statuses (pending, approved, suspended, rejected)."
          testId="overview-suppliers"
        />
        <KpiCard
          label="Customers"
          value={stats?.customers ?? "—"}
          icon={UserCircle2}
          info="Total registered customer accounts, excluding deleted accounts."
          testId="overview-customers"
        />
        <KpiCard
          label="Bookings"
          value={stats?.bookings ?? "—"}
          icon={Receipt}
          info="Total number of bookings created across all statuses."
          testId="overview-bookings"
        />
        <KpiCard
          label="Cancelled by Customer"
          value={stats?.cancelled_by_customer ?? "—"}
          icon={UserX}
          info="Bookings cancelled at the customer's request."
          testId="overview-cancelled-customer"
        />
        <KpiCard
          label="Cancelled by Supplier"
          value={stats?.cancelled_by_supplier ?? "—"}
          icon={UserX}
          info="Bookings cancelled by the supplier — often a quality concern."
          testId="overview-cancelled-supplier"
        />
      </section>

      <section className="mt-8">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Supplier categories</h2>
          <InfoTooltip text="Breakdown of suppliers across business types. Use this to monitor mix and reach out to under-represented segments." />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(SUPPLIER_CATEGORY_LABELS).map(([key, label]) => (
            <div key={key} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between" data-testid={`supplier-cat-${key}`}>
              <div>
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-2xl font-semibold tracking-tight tabular-nums mt-1">
                  {stats?.supplier_categories?.[key] ?? "—"}
                </div>
              </div>
              <Building2 className="w-4 h-4 text-muted-foreground/60" strokeWidth={1.5} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
